import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { SearchLg, X } from '@untitledui/icons';
import { en } from '../../locales/en';
import { Text } from './Text';
import { Icon } from './Icon';
import { IconButton } from '../buttons/IconButton';

interface DropDownSearchListProps<T> {
  items: T[];
  onItemSelected: (item: T | null) => void;
  selectedItem?: T | null;
  matchesQuery: (item: T, query: string) => boolean;
  getItemKey?: (item: T, index: number) => string | number;
  renderResultRow: (item: T) => ReactNode;
  renderSelectedItem: (item: T) => ReactNode;
  placeholder?: string;
  noResultsText?: string;
  disabled?: boolean;
  dropdownMaxHeight?: string;
  style?: CSSProperties;
}

function DropDownSearchList<T>({
  items,
  onItemSelected: onSelect,
  selectedItem = null,
  matchesQuery,
  getItemKey,
  renderResultRow,
  renderSelectedItem,
  placeholder,
  noResultsText = 'No matches found',
  disabled = false,
  dropdownMaxHeight = '220px',
  style
}: DropDownSearchListProps<T>) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, []);

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim();

    const matchingItems =
      normalizedQuery.length === 0 ? items : items.filter((item) => matchesQuery(item, normalizedQuery));

    return matchingItems;
  }, [items, query, matchesQuery]);

  const onResultItemSelected = (item: T) => {
    onSelect(item);
    setQuery('');
    setIsOpen(false);
  };

  const onClearSelectedItemClicked = () => {
    onSelect(null);
    setQuery('');
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        ...style
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          border: 'var(--card-border-stroke) var(--card-border-style) var(--card-border-color)',
          borderRadius: 'var(--card-border-radius)',
          padding: '8px',
          backgroundColor: '#ffffff',
          opacity: disabled ? 0.6 : 1,
          height: '48px'
        }}
      >
        {selectedItem ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              flex: 1,
              minWidth: 0
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>{renderSelectedItem(selectedItem)}</div>
            {!disabled && (
              <IconButton
                icon={X}
                onClick={onClearSelectedItemClicked}
                title={en.common.clearSelection}
                iconSize={20}
                style={{ flexShrink: 0 }}
              />
            )}
          </div>
        ) : (
          <>
            <input
              type='text'
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={placeholder}
              disabled={disabled}
              style={{
                padding: '0px',
                fontSize: '14px',
                border: 'none',
                outline: 'none',
                fontFamily: 'inherit',
                backgroundColor: 'transparent',
                cursor: disabled ? 'not-allowed' : 'text',
                width: '100%'
              }}
            />
            <div style={{ padding: '4px', display: 'flex', alignItems: 'center' }}>
              <Icon icon={SearchLg} size={20} />
            </div>
          </>
        )}
      </div>

      {isOpen && !disabled && !selectedItem && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            backgroundColor: '#ffffff',
            border: 'var(--card-border-stroke) var(--card-border-style) var(--card-border-color)',
            borderRadius: 'var(--card-border-radius)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
            zIndex: 1000,
            maxHeight: dropdownMaxHeight,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px'
          }}
        >
          {filteredItems.length === 0 ? (
            <Text as='div' variant='body2' style={{ color: 'var(--text-secondary)', padding: '8px' }}>
              {noResultsText}
            </Text>
          ) : (
            filteredItems.map((item, index) => (
              <button
                key={getItemKey ? getItemKey(item, index) : index}
                onClick={() => onResultItemSelected(item)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {renderResultRow(item)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default DropDownSearchList;
